import {
  CloseSmall,
  Delete,
  MaterialDownload,
  MoreSettings,
} from '@shared/assets/icons/components';
import { SettingOutlined, SyncOutlined } from '@ant-design/icons';
import type { KnowledgeDataType, KnowledgeSyncTimeEnum } from '@shared/api';
import {
  useKnowledgeManualSyncKnowledgeLinkedDocumentSyncPostMutation,
  useKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostMutation,
} from '@shared/api';
import { Button, Flex, Modal, Popover, Radio } from 'antd';
import classNames from 'classnames';
import type { FC } from 'react';
import { useState } from 'react';
import styles from './settings.module.scss';
import { useTranslation } from 'react-i18next';
import { Text } from '@shared/ui/antd';
import { KNOWLEDGE_SYNC_PERIODS } from '@modules/knowledge-base/lib/sync-periods';
import { useNotify } from '@shared/hooks/use-notify';

interface SettingsProps {
  knowledgeBaseId: number;
  dataType: KnowledgeDataType;
  syncPeriod?: KnowledgeSyncTimeEnum | null;
  onDeleteKnowledgeBase: (knowledgeBaseId: number) => void;
  onDownloadKnowledgeBase: (knowledgeBaseId: number) => void;
  disabled?: boolean;
}

export const Settings: FC<SettingsProps> = ({
  knowledgeBaseId,
  dataType,
  syncPeriod,
  onDeleteKnowledgeBase,
  onDownloadKnowledgeBase,
  disabled = false,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'knowledgeBase.settings' });
  const { t: tKnowledgeBase } = useTranslation('translation', { keyPrefix: 'knowledgeBase' });
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [isSyncPeriodModalOpen, setIsSyncPeriodModalOpen] = useState(false);
  const [selectedSyncPeriod, setSelectedSyncPeriod] = useState<KnowledgeSyncTimeEnum>(
    syncPeriod ?? 'DISABLE',
  );
  const [updateSyncPeriod, { isLoading: isSyncPeriodUpdating }] =
    useKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostMutation();
  const [syncKnowledgeBase, { isLoading: isSyncing }] =
    useKnowledgeManualSyncKnowledgeLinkedDocumentSyncPostMutation();

  const handleOpenChange = (newOpen: boolean) => {
    if (disabled) return;
    setOpen(newOpen);
  };

  const handleClick = (callback: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    callback();
  };

  const handleOpenSyncPeriodModal = () => {
    setSelectedSyncPeriod(syncPeriod ?? 'DISABLE');
    setIsSyncPeriodModalOpen(true);
  };

  const handleCloseSyncPeriodModal = () => {
    if (isSyncPeriodUpdating) return;
    setIsSyncPeriodModalOpen(false);
  };

  const handleSaveSyncPeriod = async () => {
    await updateSyncPeriod({
      bodyKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePost: {
        knowledge_base_id: knowledgeBaseId,
        sync_period: selectedSyncPeriod,
      },
    }).unwrap();

    setIsSyncPeriodModalOpen(false);
  };

  const handleSync = async () => {
    if (isSyncing) return;

    try {
      await syncKnowledgeBase({
        bodyKnowledgeManualSyncKnowledgeLinkedDocumentSyncPost: {
          knowledge_base_id: knowledgeBaseId,
        },
      }).unwrap();

      notify({ type: 'success', message: t('syncSuccess') });
    } catch {}
  };

  const icons = [
    ...(dataType === 'linked_document'
      ? [
          {
            key: 'update' as const,
            icon: <SyncOutlined spin={isSyncing} />,
            title: t('update'),
            onClick: handleSync,
          },
          {
            key: 'syncPeriod' as const,
            icon: <SettingOutlined />,
            title: t('syncPeriod.action'),
            onClick: handleOpenSyncPeriodModal,
          },
        ]
      : []),
    {
      key: 'download' as const,
      icon: <MaterialDownload />,
      title: t('download'),
      onClick: () => onDownloadKnowledgeBase(knowledgeBaseId),
    },
    {
      key: 'delete' as const,
      icon: <Delete />,
      title: t('delete'),
      onClick: () => onDeleteKnowledgeBase(knowledgeBaseId),
    },
  ];

  const menu = (
    <Flex vertical>
      {icons.map(
        (item) =>
          item.onClick && (
            <Flex
              key={item.key}
              align="center"
              justify="start"
              className={styles.root}
              onClick={handleClick(item.onClick)}
              gap="8px"
            >
              {item.icon}
              <Text variant="sm" fontWeight={600}>
                {item.title}
              </Text>
            </Flex>
          ),
      )}
    </Flex>
  );

  return (
    <>
      <Popover
        content={menu}
        trigger="click"
        open={disabled ? false : open}
        onOpenChange={handleOpenChange}
        arrow={false}
      >
        <Flex
          justify="center"
          onClick={(e) => e.stopPropagation()}
          align="center"
          className={classNames(styles.container, { [styles.disabled]: disabled })}
        >
          <MoreSettings />
        </Flex>
      </Popover>

      <Modal
        centered
        open={isSyncPeriodModalOpen}
        onCancel={handleCloseSyncPeriodModal}
        destroyOnClose
        width={480}
        title={null}
        closeIcon={<CloseSmall />}
        footer={null}
        styles={{
          content: {
            background: 'var(--color-bg-container)',
            border: '1px solid var(--color-border-soft)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-dropdown)',
          },
          body: {
            background: 'transparent',
          },
        }}
      >
        <Flex vertical gap={20} className={styles.modalRoot}>
          <Flex vertical gap={8}>
            <Text variant="md" fontWeight={500} className={styles.modalTitle}>
              {t('syncPeriod.title')}
            </Text>
            <Text variant="sm" className={styles.modalDescription}>
              {t('syncPeriod.description')}
            </Text>
          </Flex>

          <Radio.Group
            value={selectedSyncPeriod}
            onChange={(event) => setSelectedSyncPeriod(event.target.value)}
            className={styles.syncPeriodGroup}
          >
            {KNOWLEDGE_SYNC_PERIODS.map((period) => (
              <Radio key={period} value={period} className={styles.syncPeriodRadio}>
                {tKnowledgeBase(`syncPeriods.${period}`)}
              </Radio>
            ))}
          </Radio.Group>

          <Flex gap={12} className={styles.modalActions}>
            <Button className={styles.cancelButton} onClick={handleCloseSyncPeriodModal} block>
              {t('syncPeriod.cancel')}
            </Button>
            <Button
              type="primary"
              className={styles.saveButton}
              onClick={handleSaveSyncPeriod}
              loading={isSyncPeriodUpdating}
              disabled={isSyncPeriodUpdating}
              block
            >
              {t('syncPeriod.save')}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
